import { invalidateGroupCommunitiesEventDispatch } from '@space-lib-events/invalidateGroupCommunitiesQuery'
import { toastEventDispatch } from '@space-lib-events/toast'
import { ToastPosition, ToastType } from '@space-lib-events/toast/enum'
import type {
  CreateGroupModalPayload,
  EditGroupModalPayload
} from '@space-lib/components/groupModal/types'
import {
  Actions,
  CommunityTypeProductConsume,
  Entities,
  EventNames,
  RequestResults,
  ScreenNames
} from '@space-lib/datahub'
import useTranslation from '@space-lib/hooks/useTranslation'
import { LanguageUtils } from '@space-lib/utils/language'
import { AuthStatus, useAuthContext } from '@space-shell/common/contexts/auth'
import useMembership from '@space-shell/common/hooks/useMembership'
import { CommunitiesEndpoints } from '@space-shell/common/services/communities'
import { queryClient } from '@space-shell/config/request'
import { RoutesPaths } from '@space-shell/core/router'
import { useSetLastViewDate } from '@space-shell/modules/communities/hooks'
import useSendDataHubConsume from '@space-shell/modules/communities/hooks/useSendDataHubConsume/useSendDataHubConsume'
import { useCreateGroupCommunity } from '@space-shell/modules/community/hooks/useCreateGroupCommunity'
import { generatePath, useNavigate } from 'react-router-dom'

import useScreenName from '../useScreenName'

export type PostGroupMutationProps = {
  payload: CreateGroupModalPayload & { currentOrder?: number }
  invalidate?: () => void
}

export type PutGroupMutationProps = {
  payload: EditGroupModalPayload
  invalidate?: () => void
}

export type DeleteGroupMutationProps = {
  groupId: number
  slug: string
}

export type EventsType = {
  payload?: CreateGroupModalPayload | EditGroupModalPayload
  groupName?: string
  actionType: Actions
}

const useMenuCommunity = () => {
  const screenName = useScreenName()
  const { authStatus } = useAuthContext()
  const { membershipSlug } = useMembership()
  const { sendDataHubConsume } = useSendDataHubConsume()
  const { setLastViewDateQuery, invalidateCommunitiesQueries } = useSetLastViewDate()
  const navigate = useNavigate()
  const {
    i18n: { language },
    t
  } = useTranslation()

  const {
    createGroupMutation: { mutate: groupMutation, isLoading: isLoadingGroupMutation },
    editGroupMutation: { mutate: editGroupMutation, isLoading: isLoadingEditGroupMutation },
    deleteGroupMutation: { mutate: removeGroupMutation, isLoading: isLoadingDeleteGroupMutation }
  } = useCreateGroupCommunity()

  const postGroupMutation = async ({ payload, invalidate }: PostGroupMutationProps) => {
    groupMutation(payload, {
      onSuccess() {
        sendDataHubConsume({
          entity: Entities.GROUP,
          action: Actions.CREATE,
          event: {
            eventName: EventNames.CREATE_GROUP,
            screenName: ScreenNames.COMMUNITY_VIEW
          }
        })

        invalidateGroupCommunitiesEventDispatch()
        queryClient.invalidateQueries([CommunitiesEndpoints.getGroupCommunities(membershipSlug)])
        queryClient.invalidateQueries([
          CommunitiesEndpoints.getGroupCommunitiesPublic(membershipSlug)
        ])
        invalidate?.()
      },
      onError(error) {
        toastEventDispatch({
          type: ToastType.DANGER,
          message: t('exception_state.service_unavailable.subtitle'),
          position: ToastPosition.BOTTOM_RIGHT
        })

        sendDataHubConsume({
          entity: Entities.GROUP,
          action: Actions.CREATE,
          event: {
            eventName: EventNames.CREATE_GROUP,
            screenName: ScreenNames.COMMUNITY_VIEW,
            result: RequestResults.FAILURE,
            failureKind: error?.message
          }
        })
      }
    })
  }

  const putGroupMutation = async ({ payload, invalidate }: PutGroupMutationProps) => {
    editGroupMutation(payload, {
      onSuccess() {
        invalidateGroupCommunitiesEventDispatch()
        queryClient.invalidateQueries([CommunitiesEndpoints.getGroupCommunities(membershipSlug)])
        queryClient.invalidateQueries([
          CommunitiesEndpoints.getGroupCommunitiesPublic(membershipSlug)
        ])
        invalidate?.()
      },
      onError() {
        toastEventDispatch({
          type: ToastType.DANGER,
          message: t('exception_state.service_unavailable.subtitle'),
          position: ToastPosition.BOTTOM_RIGHT
        })
      }
    })
  }

  const deleteGroupMutation = async (payload: DeleteGroupMutationProps) => {
    removeGroupMutation(payload, {
      onSuccess() {
        sendDataHubConsume({
          entity: Entities.GROUP,
          action: Actions.DELETE,
          event: {
            eventName: EventNames.DELETE_GROUP,
            screenName: ScreenNames.COMMUNITY_VIEW,
            groupId: payload.groupId
          }
        })

        invalidateGroupCommunitiesEventDispatch()
        queryClient.invalidateQueries([CommunitiesEndpoints.getGroupCommunities(membershipSlug)])
        queryClient.invalidateQueries([
          CommunitiesEndpoints.getGroupCommunitiesPublic(membershipSlug)
        ])
      },
      onError(error) {
        sendDataHubConsume({
          entity: Entities.GROUP,
          action: Actions.DELETE,
          event: {
            eventName: EventNames.DELETE_GROUP,
            screenName: ScreenNames.COMMUNITY_VIEW,
            result: RequestResults.FAILURE,
            failureKind: error?.message,
            groupdId: payload.groupId
          }
        })
      }
    })
  }

  const handleCommunityClick = async (
    communityId: number,
    communityName: string,
    communityType: number
  ) => {
    const normalizedLanguage = LanguageUtils.formatLanguage(language).toLowerCase()

    sendDataHubConsume({
      entity: Entities.COMMUNITY,
      action: Actions.VIEW,
      event: {
        screenName,
        eventName: EventNames.VIEW_COMMUNITY_FEED,
        communityId,
        communityName,
        communityType: CommunityTypeProductConsume[communityType]
      }
    })

    if (authStatus === AuthStatus.AUTHENTICATED) {
      await setLastViewDateQuery.mutateAsync(communityId)
    }

    invalidateCommunitiesQueries(membershipSlug, communityId)

    const communityPath = `${RoutesPaths.HOME}${RoutesPaths.COMMUNITY}/:communityId`
    const path = generatePath(communityPath, {
      lang: normalizedLanguage,
      membership: membershipSlug,
      communityId: String(communityId)
    })

    return navigate(path)
  }

  return {
    putGroupMutation,
    postGroupMutation,
    deleteGroupMutation,
    handleCommunityClick,
    isLoadingGroupMutation,
    isLoadingEditGroupMutation,
    isLoadingDeleteGroupMutation
  }
}

export default useMenuCommunity
